import api from './api';
import type {
    HedgeActiveStats,
    HedgeConfig,
    HedgeConfigUpdate,
    HedgeExecution,
    HedgeHistoryQuery,
    HedgeHistoryStats,
    HedgeKlineSnapshot,
    HedgePosition,
    UserHedgeConfig,
    UserHedgeConfigUpdate,
} from '../types/hedge';
import type {PageResponse} from '../types/researchReport';

/** 对冲系统级配置（总闸/调度参数） */
export async function getHedgeConfig(): Promise<HedgeConfig | null> {
    const res = await api.get('/api/v1/hedge/config');
    return res.data?.data ?? null;
}

/** 更新系统总闸（ADMIN；业务参数走 updateUserHedgeConfig） */
export async function updateHedgeConfig(request: HedgeConfigUpdate): Promise<HedgeConfig | null> {
    const res = await api.put('/api/v1/hedge/config', request);
    return res.data?.data ?? null;
}

/** 我的品种配置（报告可见 + 对冲品种 + 对冲参数；无行则后端懒初始化） */
export async function getUserHedgeConfig(): Promise<UserHedgeConfig | null> {
    const res = await api.get('/api/v1/user-hedge-config');
    return res.data?.data ?? null;
}

/** 更新我的品种配置（空字段不修改；越界由后端拒绝） */
export async function updateUserHedgeConfig(request: UserHedgeConfigUpdate): Promise<UserHedgeConfig | null> {
    const res = await api.put('/api/v1/user-hedge-config', request);
    return res.data?.data ?? null;
}

/** 可选品种（系统报告白名单；用户只能在其中勾选） */
export async function getAvailableSymbols(): Promise<string[]> {
    const res = await api.get('/api/v1/user-hedge-config/available-symbols');
    return res.data?.data ?? [];
}

/** 逗号分隔品种串解析（trim+大写；与后端 SymbolListUtils 同口径） */
export const parseSymbolList = (text?: string | null): string[] =>
    (text ?? '')
        .split(',')
        .map(s => s.trim().toUpperCase())
        .filter(Boolean);

/** 研报对应执行记录（含交易对明细与可执行性；无记录返回 null） */
export async function getHedgeExecutionByReport(reportId: number): Promise<HedgeExecution | null> {
    const res = await api.get(`/api/v1/hedge/report/${reportId}/execution`);
    return res.data?.data ?? null;
}

/** 手动执行对冲交易对（研报页「执行对冲」按钮；后端校验最新研报 + 时间窗口 + 待执行状态） */
export async function executeHedgeReport(reportId: number): Promise<string | null> {
    const res = await api.post(`/api/v1/hedge/report/${reportId}/execute`);
    return res.data?.data ?? null;
}

/** 运行中持仓列表（创建时间倒序；结束记录走 getHedgeHistoryPositions 分页） */
export async function getHedgePositions(limit: number = 100): Promise<HedgePosition[]> {
    const res = await api.get('/api/v1/hedge/positions', {params: {limit}});
    return res.data?.data ?? [];
}

/** 运行中持仓统计（运行中 tab 统计行；后端聚合全部运行中记录） */
export async function getHedgeActiveStats(): Promise<HedgeActiveStats | null> {
    const res = await api.get('/api/v1/hedge/positions/stats');
    return res.data?.data ?? null;
}

/** 结束态持仓筛选分页（结束 tab 瀑布流；from/to 为 yyyy-MM-dd HH:mm:ss） */
export async function getHedgeHistoryPositions(
    query: HedgeHistoryQuery,
    page: number,
    size: number = 20,
): Promise<PageResponse<HedgePosition>> {
    const res = await api.get('/api/v1/hedge/positions/history', {params: {...query, page, size}});
    return res.data?.data ?? {content: [], totalElements: 0, totalPages: 0, number: page, size};
}

/** 结束态命中统计（统计行；后端按筛选条件对全部命中记录聚合） */
export async function getHedgeHistoryStats(query: HedgeHistoryQuery): Promise<HedgeHistoryStats | null> {
    const res = await api.get('/api/v1/hedge/positions/history/stats', {params: query});
    return res.data?.data ?? null;
}

/** 品种执行记录分页 */
export async function getHedgeExecutions(
    symbol: string,
    page: number = 0,
    size: number = 20,
): Promise<PageResponse<HedgeExecution>> {
    const res = await api.get('/api/v1/hedge/executions', {params: {symbol, page, size}});
    return res.data?.data ?? {content: [], totalElements: 0, totalPages: 0, number: 0, size};
}

/** 手动平仓（双腿市价平掉） */
export async function closeHedgePosition(id: number): Promise<HedgePosition | null> {
    const res = await api.post(`/api/v1/hedge/positions/${id}/close`);
    return res.data?.data ?? null;
}

/** 手动补单（对冲腿补到目标比例） */
export async function rebalanceHedgePosition(id: number): Promise<HedgePosition | null> {
    const res = await api.post(`/api/v1/hedge/positions/${id}/rebalance`);
    return res.data?.data ?? null;
}

/** 手动重试（按持仓状态选择补偿退出/补单/续平） */
export async function retryHedgePosition(id: number): Promise<HedgePosition | null> {
    const res = await api.post(`/api/v1/hedge/positions/${id}/retry`);
    return res.data?.data ?? null;
}

/** 结束态持仓 K 线快照（离线数据；无快照的历史仓后端会按需回补一次） */
export async function getHedgePositionKline(
    positionId: number,
    market: 'SPOT' | 'SWAP',
    timeframe: string,
): Promise<HedgeKlineSnapshot> {
    const res = await api.get(`/api/v1/hedge/positions/${positionId}/kline`, {
        params: {market, timeframe},
    });
    return res.data?.data ?? {positionId, market, timeframe, candles: []};
}
